import {
  Card,
  Container,
  Eyebrow,
  Lead,
  ProductTile,
  ScrollCue,
  Section,
  SectionTitle,
  StepArrow,
} from "@vcn/ui";
import { useContent } from "../content";

/**
 * Drei Linien von der Kachel hinunter zu den Phasen. Kurven 1:1 aus
 * „Element 527/528/529.svg“ (Bogen 334,79 bzw. 330,88 breit, 155,72 hoch,
 * Strichstärke 2, Punkte r 4,51) – gesetzt aber in den Koordinaten der
 * Balkengruppe (940 × 160 Einheiten).
 *
 * Das ist der springende Punkt: im Entwurf enden die drei Linien exakt auf
 * den Balkenmitten (gemessen 570 / 959,8 / 1349,7 gegen die Balkenmitten
 * 570 / 960 / 1350), also bei 80, 470 und 860 dieser Gruppe – nicht an
 * deren Aussenkanten. Der Fächer spannt damit nur 83 % der Gruppenbreite.
 * Die Boegen verlassen die Kachel senkrecht und laufen unten ebenso
 * senkrecht in die Punkte.
 */
function PhaseFan() {
  return (
    <svg
      viewBox="0 -1 940 162"
      aria-hidden="true"
      className="mx-auto h-auto w-full"
      fill="none"
    >
      <g stroke="#ccffce" strokeWidth="2" strokeMiterlimit="10">
        <path d="M414.79,0C414.79,118.54,87.93,41.91,80,155.72" />
        <path d="M470,0V155.49" />
        <path d="M529.12,0C529.12,118.54,852.14,41.91,860,155.72" />
      </g>
      <g fill="#ccffce">
        <circle cx="80" cy="155.49" r="4.51" />
        <circle cx="470" cy="155.49" r="4.51" />
        <circle cx="860" cy="155.49" r="4.51" />
      </g>
    </svg>
  );
}

export function Process() {
  const { process } = useContent();

  return (
    <Section id="ablauf" className="scroll-mt-20">
      <Container>
        <div className="text-center">
          <Eyebrow>{process.eyebrow}</Eyebrow>
          <SectionTitle className="mt-4">
            {process.title.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </SectionTitle>
          <Lead className="mx-auto mt-6 max-w-3xl">{process.body}</Lead>
        </div>

        <p className="mt-16 text-center text-xl font-bold text-white sm:text-2xl">
          {process.actor}
        </p>

        <div
          aria-hidden="true"
          className="mx-auto mt-4 h-12 w-px bg-[var(--brand-line)] sm:h-20"
        />

        <ProductTile
          brand="carrierhub"
          width="clamp(6rem, 15vw, 10rem)"
          shadow={false}
          decorative
          className="mt-4"
        />

        {/*
          Fächer und Balken teilen sich denselben Container, damit die
          Linienenden exakt auf den Balkenmitten sitzen. Breiten aus dem
          Entwurf: 161 / 601 / 161 Einheiten in einer 938 breiten Gruppe.
        */}
        <div
          // `inline-size`: die Beschriftung unten misst sich in `cqw` an
          // dieser Gruppe – so wächst sie stufenlos mit den Balken mit,
          // statt an Breakpoints zu springen.
          style={{ containerType: "inline-size" }}
          className="mx-auto mt-4 w-full max-w-[58.6rem] sm:w-[min(48.8vw,58.6rem)]"
        >
          <PhaseFan />

          {/*
            Beschriftung und Balken stehen im Entwurf im Verhältnis 36 zu 50
            Einheiten, der Abstand von der Schriftlinie zur Balkenoberkante
            beträgt knapp die halbe Balkenhöhe. Die Balken sind dort keine
            Pillen: ihr Eckradius ist 20 von 50 Einheiten (Element 530–532).
          */}
          <ol className="mt-4 flex items-end justify-between gap-[0.8%]">
            {process.phases.map((phase, index) => (
              <li
                key={phase.label}
                // `min-w-0`: sonst zieht das breitere Label („Beauftragung“)
                // seinen Flex-Kasten auseinander und der Balken würde breiter.
                className="min-w-0 text-center"
                style={{ width: phase.width }}
              >
                {/*
                  „Beauftragung“ ist im Entwurf 246 Einheiten breit, sein
                  Balken nur 160 – der Text ragt dort links und rechts je 43
                  Einheiten über. `w-max` plus 50-%-Versatz hält ihn exakt
                  mittig über dem Balken, statt ihn nur nach rechts überlaufen
                  zu lassen.

                  Mobil ist dafür kein Platz: dort rückt der Entwurf die
                  äußeren Beschriftungen nach innen, damit sie nicht über den
                  Seitenrand laufen. Deshalb hängt die erste am linken und die
                  letzte am rechten Balkenende; ab sm sind alle drei mittig.
                */}
                <p
                  className={[
                    // Im Entwurf ist die Beschriftung rund 4,5 % der
                    // Gruppenbreite (36 von 940 Einheiten auf dem Desktop,
                    // 16 von 335 mobil). Über `cqw` gilt das an jeder
                    // Fensterbreite – gedeckelt auf die Desktop-Größe.
                    "relative w-max text-[clamp(0.75rem,4.5cqw,1.65rem)] leading-tight font-bold text-white",
                    "sm:left-1/2 sm:-translate-x-1/2",
                    index === 0
                      ? "left-0"
                      : index === process.phases.length - 1
                        ? "left-full -translate-x-full"
                        : "left-1/2 -translate-x-1/2",
                  ].join(" ")}
                >
                  {phase.label}
                </p>
                <span
                  aria-hidden="true"
                  // Balken mobil 40 hoch mit Radius 12 (Element 567/568),
                  // auf dem Desktop 50 mit Radius 20 (Element 530–532).
                  className="mt-2 block h-10 rounded-[0.75rem] bg-linear-to-r from-[var(--brand-phase-from)] to-[var(--brand-phase-to)] sm:h-9 sm:rounded-[0.9rem]"
                />
              </li>
            ))}
          </ol>
        </div>

        <ScrollCue className="mt-10" />

        <ol className="mt-10 grid gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:gap-0">
          {process.steps.map((step, index) => (
            <li key={step.title} className="contents">
              <Card className="text-center">
                <h3 className="text-lg font-bold text-balance text-white">
                  {step.title}
                </h3>
                <p className="mt-3 text-[0.92rem] leading-relaxed text-pretty text-white/85">
                  {step.body}
                </p>
              </Card>
              {index < process.steps.length - 1 && (
                <div className="flex items-center justify-center py-2 md:px-4 md:py-0">
                  <StepArrow className="rotate-90 md:rotate-0" />
                </div>
              )}
            </li>
          ))}
        </ol>
      </Container>
    </Section>
  );
}
